import React, { useEffect } from 'react';

import cn from 'classnames';
import { Controller } from 'react-hook-form';

import { TEmployerId } from '@entities/modal/model/types/employerId';
import { useCreateCustomTaskQuery } from '@entities/task/hooks/useCreateCustomTaskQuery';
import { showToast } from '@features/ToastManager';
import { useHandleForm } from '@shared/hooks';
import { Form, Input, ModalTitle, UiButton, UiDateInput } from '@shared/ui';

import cls from './AssignTaskToEmployee.module.css';
import { useAssignTaskToEmployee } from './lib/hooks';
import { defaultValues } from './model/data';
import { IFormValues } from './model/types';
import { AssignTaskToEmployeePopover } from './ui/AssignTaskToEmployeePopover';

interface AssignTaskToEmployeeProps {
    currentClickedId: TEmployerId;
    onCloseCb: () => void;
    classes?: string;
}

export const AssignTaskToEmployee = ({
    onCloseCb,
    currentClickedId,
    classes,
}: AssignTaskToEmployeeProps) => {
    const { t, triggerAnimationError, setTriggerAnimationError, executorId } =
        useAssignTaskToEmployee(currentClickedId);
    const createTaskMutation = useCreateCustomTaskQuery(() =>
        showToast(t('AssignTaskToEmployee.OnSuccessPush.text'))
    );

    const { handleSubmit, control, errors, isSubmitting } = useHandleForm<IFormValues>({
        onSubmit: (data) => {
            const { taskDescription, taskTitle, taskDate } = data;
            createTaskMutation.mutate({
                title: taskTitle,
                description: taskDescription,
                date: taskDate,
                executorId,
            });
            onCloseCb?.();
        },
        defaultValues,
    });

    useEffect(() => {
        if (errors.taskTitle) {
            setTriggerAnimationError(true);
        }
    }, [errors.taskTitle, setTriggerAnimationError]);

    return (
        <AssignTaskToEmployeePopover onCloseCb={onCloseCb} currentClickedId={currentClickedId}>
            <Form
                className={cn(cls.AssignTaskToEmployee, [classes])}
                onSubmit={handleSubmit}
                id="assign-task-to-employee-form"
            >
                <Controller
                    name="taskTitle"
                    control={control}
                    rules={{
                        required: true,
                        maxLength: {
                            value: 255,
                            message: t('AssignTaskToEmployee.TaskTitle.Error.MaxLength'),
                        },
                    }}
                    render={({ field }) => (
                        <ModalTitle
                            {...field}
                            isError={!!errors.taskTitle}
                            onChangeHandler={field.onChange}
                            value={field.value}
                            triggerAnimationError={triggerAnimationError}
                            setTriggerAnimationError={setTriggerAnimationError}
                            autoFocus
                            classes={cls.ModalTitleClassName}
                            errorClassName={cls.ErrorClassName}
                            placeholder={t('AssignTaskToEmployee.TaskTitle.placeholder')}
                        />
                    )}
                />
                <Controller
                    name="taskDescription"
                    control={control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            classes={[cls.DescriptionInputWrapper]}
                            inputClassName={cls.DescriptionInputClassName}
                            placeholder={t('AssignTaskToEmployee.TaskDescription.placeholder')}
                            title={field.value}
                            value={field.value}
                        />
                    )}
                />

                <Controller
                    name="taskDate"
                    control={control}
                    render={({ field }) => (
                        <UiDateInput
                            {...field}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('AssignTaskToEmployee.DateInput.placeholder')}
                            error={{ isError: false }}
                            inputClassName={cls.DateInputClassName}
                            iconFill="#1094F7"
                        />
                    )}
                />
                <UiButton
                    type="submit"
                    form="assign-task-to-employee-form"
                    btnColor="primary"
                    btnText={t('button.save.text')}
                    disabled={!!errors.taskTitle || isSubmitting}
                />
            </Form>
        </AssignTaskToEmployeePopover>
    );
};
